import { useState } from "react";
import { afterAuthPath } from "@/lib/guest";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Lock, Mail, Phone, User } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Btn, Field } from "@/components/kit";
import BusinessFields, { cleanBusiness, validateBusiness } from "@/components/BusinessFields";
import OtpStep from "@/components/OtpStep";
import AuthLayout from "@/components/AuthLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Section = ({ n, title, testid }) => (
  <div className="flex items-center gap-3 pt-2" data-testid={testid}>
    <span className="w-7 h-7 rounded-full bg-[#E5E5FD] text-[#0000EC] text-[13px] font-extrabold flex items-center justify-center">{n}</span>
    <h2 className="text-[17px] font-bold">{title}</h2>
    <span className="flex-1 h-px bg-[#E2E8F0]" />
  </div>
);

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [business, setBusiness] = useState({ businessCategoryId: "", companyName: "", designation: "" });
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullPhone, setFullPhone] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const next = validateBusiness(business);
    if (f.name.trim().length < 2) next.name = "Please enter your full name.";
    if (!EMAIL_RE.test(f.email.trim())) next.email = "Enter a valid email address.";
    if (f.phone.replace(/\D/g, "").length < 7) next.phone = "Enter a valid mobile number.";
    if (f.password.length < 6) next.password = "Password must be at least 6 characters.";
    if (f.confirm !== f.password) next.confirm = "Passwords do not match.";
    if (!agreed) next.agree = "Please accept the Terms of Service and Privacy Policy to continue.";
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length) return setFormError("Please complete the highlighted fields above.");
    setLoading(true);
    try {
      const p = `+91${f.phone.replace(/\D/g, "")}`;
      await api.post("/auth/send-otp", { phone: p });
      setFullPhone(p);
    } catch (e) {
      setFormError(e.message || "Couldn't send the code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (code) => {
    setOtpError(null);
    setVerifying(true);
    try {
      const r = await api.post("/auth/verify-otp", { phone: fullPhone, code });
      if (!r?.verified) throw new Error("Incorrect or expired code.");
      await register({ name: f.name.trim(), email: f.email.trim(), phone: fullPhone, password: f.password, ...cleanBusiness(business) });
      nav(afterAuthPath());
    } catch (e) {
      setOtpError(e.message || "Incorrect code, try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <AuthLayout wide={!fullPhone} topRight={<>Already have an account?&nbsp;<Link to="/login" className="t-link text-[14px]" data-testid="top-go-to-login">Log in</Link></>}>
      <button className="-ml-2 mb-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={() => (fullPhone ? setFullPhone(null) : nav("/login"))} data-testid="register-back"><ChevronLeft className="w-6 h-6" strokeWidth={2.4} /></button>
      {fullPhone ? <OtpStep phone={fullPhone} onVerify={verify} verifying={verifying} error={otpError} prefix="register" /> : (
        <>
          <h1 className="text-[28px] lg:text-[34px] font-extrabold" data-testid="register-title">Create your account</h1>
          <p className="text-[15px] text-[#475569] mt-0.5 mb-7">Free to start. We will verify your mobile number with a 4-digit code.</p>
          <div className="flex flex-col gap-5">
            <Section n="1" title="About you" />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full Name *" icon={User} placeholder="Sai Kumar" value={f.name} onChange={set("name")} error={errors.name} maxLength={120} testid="register-name" className="sm:col-span-2" />
              <Field label="Email *" icon={Mail} type="email" placeholder="you@company.com" value={f.email} onChange={set("email")} error={errors.email} testid="register-email" />
              <Field label="Mobile Number *" icon={Phone} inputMode="tel" placeholder="9876543210" value={f.phone} onChange={set("phone")} error={errors.phone} testid="register-phone" />
            </div>
            <Section n="2" title="Business details" testid="register-business-heading" />
            <div className="grid gap-5 sm:grid-cols-2 [&>label:first-child]:sm:col-span-2">
              <BusinessFields prefix="register" value={business} onChange={setBusiness} errors={errors} />
            </div>
            <Section n="3" title="Secure your account" />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Password *" icon={Lock} type="password" placeholder="At least 6 characters" value={f.password} onChange={set("password")} error={errors.password} testid="register-password" />
              <Field label="Confirm Password *" icon={Lock} type="password" placeholder="Re-enter password" value={f.confirm} onChange={set("confirm")} error={errors.confirm} testid="register-confirm" />
            </div>
            {formError && <p className="text-[14px] text-[#DC2626]" data-testid="register-error">{formError}</p>}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 w-5 h-5 rounded-md" data-testid="register-agree-toggle" />
              <span className="text-[14px] text-[#475569]">I agree to the <Link to="/legal/terms" target="_blank" className="t-link">Terms of Service</Link> and <Link to="/legal/privacy" target="_blank" className="t-link">Privacy Policy</Link>.</span>
            </label>
            {errors.agree && <p className="text-[13px] text-[#DC2626] -mt-3" data-testid="register-agree-error">{errors.agree}</p>}
            <Btn onClick={submit} loading={loading} testid="register-submit">Send OTP</Btn>
          </div>
          <p className="mt-8 text-center text-[15px] text-[#475569]">Already have an account? <Link to="/login" className="t-link text-[15px]" data-testid="go-to-login">Login</Link></p>
        </>
      )}
    </AuthLayout>
  );
}
